import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { serviceTypeService } from '../../../services/serviceTypeService';
import { carHouseService } from '../../../services/carHouseService';

const CreateOrderModal = ({ 
  isOpen, 
  onClose, 
  vehicleData, 
  onCreateOrder, 
  isLoading = false 
}) => {
  const [serviceType, setServiceType] = useState('');
  const [selectedCarHouse, setSelectedCarHouse] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [color, setColor] = useState('');
  const [notes, setNotes] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [modifiedModel, setModifiedModel] = useState('');
  const [modifiedYear, setModifiedYear] = useState('');

  // Service type options will be loaded dynamically
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  // Car house options will be loaded dynamically
  const [carHouseOptions, setCarHouseOptions] = useState([]);

  // Reset form when modal opens/closes and load data
  useEffect(() => {
    if (isOpen) {
      setServiceType('');
      setSelectedCarHouse('');
      setLicensePlate('');
      setColor('');
      setNotes('');
      setReturnDate('');
      setReturnTime('');
      setModifiedModel(vehicleData?.model || '');
      setModifiedYear(vehicleData?.year || '');
      loadServiceTypes();
      loadCarHouses();
    }
  }, [isOpen, vehicleData]);

  const loadServiceTypes = async () => {
    try {
      const { data, error } = await serviceTypeService.getServiceTypeOptions();
      if (error) {
        console.error('Error loading service types:', error);
        setServiceTypeOptions([]);
      } else {
        setServiceTypeOptions(data || []);
      }
    } catch (err) {
      console.error('Error loading service types:', err);
      setServiceTypeOptions([]);
    }
  };



  const loadCarHouses = async () => {
    try {
      const { data, error } = await carHouseService.getAllCarHouses({ status: 'active' });
      if (error) {
        console.error('Error loading car houses:', error);
        setCarHouseOptions([]);
      } else {
        // Transform car house data to match Select component format
        const transformedCarHouses = (data || []).map(carHouse => ({
          value: carHouse.id,
          label: `${carHouse.name} - ${carHouse.city}`
        }));
        setCarHouseOptions(transformedCarHouses);
      }
    } catch (err) {
      console.error('Error loading car houses:', err);
      setCarHouseOptions([]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!serviceType || !selectedCarHouse) {
      alert('Bitte wählen Sie einen Service-Typ und ein Autohaus aus.');
      return;
    }

    // Get service type display name and price
    const selectedServiceType = serviceTypeOptions.find(st => st.value === serviceType);
    const serviceTypeName = selectedServiceType?.label || 'Service';
    const serviceTypePrice = selectedServiceType?.price || 0;

    const orderData = {
      vehicleData: {
        ...vehicleData,
        model: modifiedModel || vehicleData?.model,
        year: modifiedYear || vehicleData?.year
      },
      serviceType,
      serviceTypeName,
      serviceTypePrice,
      selectedCarHouse,
      licensePlate,
      color,
      notes,
      returnDate,
      returnTime,
      status: 'NEW',
      createdAt: new Date().toISOString()
    };

    onCreateOrder(orderData);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">Neuen Auftrag erstellen</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Serviceauftrag für {vehicleData?.make} {vehicleData?.model} ({vehicleData?.vin})
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="p-6 border-b border-border bg-muted/30">
          <h3 className="text-sm font-semibold text-foreground mb-3">Fahrzeugdaten</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Marke:</span>
              <p className="font-medium">{vehicleData?.make}</p>
            </div>
            <div>
              <span className="text-muted-foreground">VIN:</span>
              <p className="font-mono text-xs">{vehicleData?.vin}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Motor:</span>
              <p className="font-medium">{vehicleData?.engine || 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Getriebe:</span>
              <p className="font-medium">{vehicleData?.transmission || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Service Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Service-Typ *
            </label>
            <Select
              value={serviceType}
              onChange={setServiceType}
              placeholder="Service-Typ auswählen"
              disabled={isLoading}
              options={serviceTypeOptions}
            />
          </div>

          {/* Vehicle Model and Year - Editable */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Modell
              </label>
              <Input
                type="text"
                value={modifiedModel}
                onChange={(e) => setModifiedModel(e.target.value)}
                placeholder="Fahrzeugmodell"
                disabled={isLoading}
              />
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Baujahr
              </label>
              <Input
                type="number"
                value={modifiedYear}
                onChange={(e) => setModifiedYear(e.target.value)}
                placeholder="Baujahr"
                min="1900"
                max={new Date().getFullYear() + 1}
                disabled={isLoading}
              />
            </div>
          </div>



          {/* Car House */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Autohaus *
            </label>
            <Select
              value={selectedCarHouse}
              onChange={setSelectedCarHouse}
              placeholder="Autohaus auswählen"
              disabled={isLoading}
              options={carHouseOptions}
            />
          </div>

          {/* License Plate */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kennzeichen
            </label>
            <Input
              type="text"
              value={licensePlate}
              onChange={(e) => setLicensePlate(e.target.value)}
              placeholder="Geben Sie das Kennzeichen ein"
              disabled={isLoading}
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Farbe
            </label>
            <Select
              value={color}
              onChange={setColor}
              placeholder="Farbe auswählen"
              disabled={isLoading}
              options={[
                { value: 'blau', label: 'Blau' },
                { value: 'grün', label: 'Grün' },
                { value: 'schwarz', label: 'Schwarz' },
                { value: 'weiß', label: 'Weiß' },
                { value: 'rot', label: 'Rot' },
                { value: 'gelb', label: 'Gelb' },
                { value: 'gold', label: 'Gold' },
                { value: 'grau', label: 'Grau' },
                { value: 'silber', label: 'Silber'},
                { value: 'braun', label: 'Braun' },
                { value: 'dunkelblau', label: 'Dunkelblau' }
              ]}
            />
          </div>

          {/* Return Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Return Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Rückgabedatum
              </label>
              <Input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Return Time */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Rückgabezeit
              </label>
              <Input
                type="time"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen zum Auftrag..."
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={!serviceType || !selectedCarHouse}
              iconName="Plus"
              iconPosition="left"
            >
              Auftrag erstellen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;
